import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AppBar, Box, Button, CircularProgress, Drawer } from '@mui/material';
import { useAuth } from './auth';
import { useBoard } from './useBoard';
import { TableView } from './views/TableView';
import { BoardView } from './views/BoardView';
import { TicketDetail } from './views/TicketDetail';
import { NewTicketDialog } from './views/NewTicketDialog';
import { ConnectAgent } from './views/ConnectAgent';
import type { Meta, Ticket } from './api';
import { useViewPrefs } from './useViewPrefs';
import { ALL, TABS, type TabKey } from './viewPrefs';
import { useAppRoute } from './useAppRoute';
import { EpicsView } from './views/EpicsView';
import { RecentView } from './views/RecentView';
import { EpicPage } from './views/EpicPage';
import { BOARD, type Route } from './route';
import { STATUS_ORDER } from './views/status';
import { makeBoardControls } from './boardControls';
import { BoardBar } from './BoardBar';

/**
 * `expanded` lives here rather than in `TicketDetail`, which is conditionally rendered and
 * so unmounts on every close, resetting anything it held. This component stays mounted for
 * the life of the board — only the Drawer's `open` changes — which is what makes full
 * screen sticky across tickets. That is deliberate: it is a reading preference, not a
 * property of one ticket, and re-expanding for every long body is the friction it removes.
 */
function TicketDrawer({
  openRef,
  meta,
  arrivedAtTicket,
  onClose,
  onSaved,
}: {
  openRef: number | null;
  meta: Meta | null;
  /** Opens expanded: a pasted link should land on the ticket, not beside it. */
  arrivedAtTicket: boolean;
  onClose: () => void;
  onSaved: (ticket: Ticket) => void;
}) {
  const [expanded, setExpanded] = useState(arrivedAtTicket);

  /**
   * The deep-link expansion is a ONE-SHOT override, not a preference change.
   *
   * `expanded` is otherwise sticky across tickets (DWLF-102), so without this, following
   * one link would leave every ticket opening full screen for the rest of the session —
   * a link quietly rewriting how the app behaves, which is not what clicking a link means.
   * Closing the ticket you arrived at hands control back; expanding by hand after that is
   * a choice, and stays sticky as before.
   */
  const overrideSpent = useRef(false);
  useEffect(() => {
    if (openRef === null && arrivedAtTicket && !overrideSpent.current) {
      overrideSpent.current = true;
      setExpanded(false);
    }
  }, [openRef, arrivedAtTicket]);
  return (
    <Drawer anchor="right" open={openRef !== null} onClose={onClose}>
      <Box sx={{ width: expanded ? '100vw' : { xs: '100vw', sm: 720 } }}>
        {openRef !== null && (
          <TicketDetail
            refNumber={openRef}
            meta={meta}
            expanded={expanded}
            onToggleExpand={() => setExpanded((current) => !current)}
            onClose={onClose}
            onSaved={onSaved}
          />
        )}
      </Box>
    </Drawer>
  );
}

/**
 * What fills the page under the toolbar.
 *
 * Driven by `base` rather than the current route: what sits BEHIND the ticket drawer is
 * the route the ticket was opened OVER. Without that, opening a ticket from an epic page
 * would swap the page underneath it back to the board while the drawer is still open.
 */
function MainView({
  base,
  tab,
  board,
  visible,
  openTicket,
  navigate,
}: {
  base: Route;
  tab: number;
  board: ReturnType<typeof useBoard>;
  visible: Ticket[];
  openTicket: (ref: number) => void;
  navigate: (route: Route) => void;
}) {
  /**
   * The columns every status-per-column view lays out, resolved once. `/v1/meta` owns the
   * vocabulary; the client constant covers the gap before it lands and a failed reload,
   * where an empty list would render a board with no columns at all under the error
   * banner — every ticket gone, with nothing saying why.
   */
  const columns = board.meta?.statuses?.length ? board.meta.statuses : STATUS_ORDER;

  if (base.kind === 'epic') {
    return (
      <EpicPage
        name={base.name}
        board={board.tickets}
        statuses={columns}
        onOpenTicket={openTicket}
        onBack={() => navigate(BOARD)}
      />
    );
  }

  /**
   * Keyed by tab NAME and typed so every tab must have one: adding a row to `TABS` stops
   * this compiling until a view is supplied. That is the guarantee — the `?? 'board'`
   * below is still a silent default, but it can only be reached by an index that is not a
   * tab at all, which `parseViewPrefs` already clamps away.
   */
  const views: Record<TabKey, () => JSX.Element> = {
    epics: () => (
      <EpicsView
        onOpenEpic={(name) => navigate({ kind: 'epic', name })}
        onChanged={() => void board.reload()}
      />
    ),
    board: () => (
      <BoardView tickets={visible} statuses={columns} onOpen={openTicket} />
    ),
    table: () => <TableView tickets={visible} onOpen={openTicket} />,
    recent: () => <RecentView tickets={visible} onOpen={openTicket} />,
  };

  const tabKey = TABS[tab]?.key ?? 'board';

  // The epics list fetches for itself, so it does not wait on the board.
  if (board.loading && tabKey !== 'epics') {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return views[tabKey]();
}

export function App() {
  const { email, signOut, expired } = useAuth();
  const board = useBoard(Boolean(email));
  const { tab, setTab, epic, setEpic, status, setStatus } = useViewPrefs(board.meta);
  const { route, base, navigate, openTicket, closeTicket, arrivedAtTicket } = useAppRoute();
  const openRef = route.kind === 'ticket' ? route.ref : null;
  const [creating, setCreating] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const controls = makeBoardControls({ base, tab, navigate, setTab, setEpic, setStatus });

  const visible = useMemo(
    () =>
      board.tickets.filter(
        (ticket) =>
          (epic === ALL || ticket.epic === epic) && (status === ALL || ticket.status === status),
      ),
    [board.tickets, epic, status],
  );

  return (
    <Box sx={{ minHeight: '100dvh' }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <BoardBar
          tab={tab}
          chooseTab={controls.chooseTab}
          filters={{ epic, status }}
          chooseFilters={controls.chooseFilters}
          meta={board.meta}
          lastUpdated={board.lastUpdated}
          refreshingSince={board.refreshingSince}
          email={email}
          signOut={signOut}
          onNew={() => setCreating(true)}
          onConnect={() => setConnecting(true)}
          onOpenEpic={(name) => navigate({ kind: 'epic', name })}
        />
      </AppBar>

      {expired && (
        <Alert severity="warning">Your session expired. Sign in again — nothing you typed is lost.</Alert>
      )}
      {board.error && (
        <Alert severity="error" action={<Button onClick={() => void board.reload()}>Retry</Button>}>
          {board.error}
        </Alert>
      )}

      <MainView
        base={base}
        tab={tab}
        board={board}
        visible={visible}
        openTicket={openTicket}
        navigate={navigate}
      />

      <NewTicketDialog
        open={creating}
        meta={board.meta}
        onClose={() => setCreating(false)}
        onCreated={(ticket) => {
          board.apply(ticket);
          openTicket(ticket.ref); // Straight into the new ticket — creating one is almost
                                  // always the first half of writing its body.
        }}
      />

      <Drawer anchor="right" open={connecting} onClose={() => setConnecting(false)}>
        <ConnectAgent onClose={() => setConnecting(false)} />
      </Drawer>

      <TicketDrawer
        openRef={openRef}
        meta={board.meta}
        arrivedAtTicket={arrivedAtTicket}
        onClose={closeTicket}
        onSaved={board.apply}
      />
    </Box>
  );
}
